import React, { useState, useEffect } from 'react';

import { Grid } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import CircleCheckedFilled from '@material-ui/icons/CheckCircle';
import CircleUnchecked from '@material-ui/icons/RadioButtonUnchecked';
import ListTable from 'components/TableComponents/ListTable';
import useAsync from 'components/hooks/useAsync';
import { useDispatch } from 'react-redux';
import ActionsContainer from 'screens/admin/Reviews/ReviewProcess/ReviewCycle/actionsContainer';
import styled from 'styled-components';
import { StyledTypography } from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { LogoWithoutNameImage } from 'constants/icons';
import theme from 'constants/theme';

import { getTemplateList, getSelectedTemplate, setTemplate } from 'services/engagement';
import { showErrorSnackbar } from 'services/snackbar';

import { DeerFooterContainer } from './DeerFooterContainer';
import SearchSurveys from './SearchSurveys';

const HeadingContainer = styled(Grid)`
  padding: 1rem 1.5rem 1rem;
  border-bottom: 1px solid ${theme.palette.borders.lighterGrey};
`;

const ListRowContainer = styled(Grid)`
  align-items: center;
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.palette.background.lightBlue};
  }
  padding: 1.5rem 1.5rem;
`;

const ROWS_PER_PAGE = 4;

const filterRows = (templateList, searchText, filterDropdownValue) => {
  let filteredValues = templateList;
  if (searchText) {
    filteredValues = filteredValues.filter((item) => item.name?.toLowerCase().indexOf(searchText.toLowerCase()) !== -1);
  }
  if (filterDropdownValue) {
    filteredValues = filteredValues.filter((item) => item.createdBy === filterDropdownValue);
  }
  return filteredValues;
};

const ListRow = ({ id, name, showMeshIcon, createdBy, questionCount, selected, clickHandler }) => (
  <ListRowContainer container onClick={clickHandler}>
    <Grid item container xs={7} alignItems="center">
      <Checkbox
        color="primary"
        icon={<CircleUnchecked />}
        checkedIcon={<CircleCheckedFilled />}
        style={{ marginRight: '1rem' }}
        checked={selected}
      />
      <StyledTypography customColor={theme.palette.text.heading}>{name}</StyledTypography>
      {showMeshIcon && (
        <img src={LogoWithoutNameImage} alt="Logo" style={{ width: '18px', height: '18px', marginLeft: '12px' }} />
      )}
    </Grid>
    <Grid item xs={3}>
      <StyledTypography customColor={theme.palette.grey.main}>{createdBy}</StyledTypography>
    </Grid>
    <Grid item xs={2}>
      <StyledTypography customColor={theme.palette.grey.main}>{questionCount}</StyledTypography>
    </Grid>
  </ListRowContainer>
);

const HeadingHeader = () => (
  <HeadingContainer container>
    <Grid item xs={7}>
      <StyledTypography customColor={theme.palette.text.label} variant="body2">
        Name
      </StyledTypography>
    </Grid>
    <Grid item xs={3}>
      <StyledTypography customColor={theme.palette.text.label} variant="body2">
        Created by
      </StyledTypography>
    </Grid>
    <Grid item xs={2}>
      <StyledTypography customColor={theme.palette.text.label} variant="body2">
        Questions
      </StyledTypography>
    </Grid>
  </HeadingContainer>
);

const getListData = (templateListData, selectedId, setSelectedId) =>
  templateListData.map((item) => ({
    id: item.id,
    comp: (
      <ListRow
        id={item.id}
        name={item.name}
        showMeshIcon={item.isSample}
        createdBy={item.createdBy}
        questionCount={item.questionCount}
        selected={item.id === selectedId}
        clickHandler={() => setSelectedId(item.id)}
      />
    ),
  }));

const TemplateTable = ({ currentSelectedTemplateId, setCurrentSelectedTemplateId }) => {
  const { data: templateList, isLoading, isIdle, run, isError, isSuccess } = useAsync();
  const [currentPage, setCurrentPage] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [filterDropdownValue, setFilterDropdownValue] = useState(null);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    run(getTemplateList({ includeSample: true }));
  }, [run]);

  const filteredData = templateList ? filterRows(templateList, searchText, filterDropdownValue) : [];
  const rowsData = getListData(filteredData, currentSelectedTemplateId, setCurrentSelectedTemplateId);
  const dropdownValues = templateList
    ? templateList.map((item) => ({
        value: item.createdBy,
        label: item.createdBy,
      }))
    : [];

  return (
    <ListTable
      success
      noDataMessage="No matching survey templates found!"
      error={isError}
      loading={isLoading}
      paginationData={{
        count: filteredData?.length || 0,
        currentPage,
        rowsPerPage: ROWS_PER_PAGE,
        setCurrentPage: handlePageChange,
      }}
      listData={rowsData}
      headingHeader={<HeadingHeader />}
      searchHeader={
        <SearchSurveys
          onSearchChange={(text) => {
            setSearchText(text);
          }}
          dropdownValues={dropdownValues}
          onDropdownChange={(value) => setFilterDropdownValue(value)}
        />
      }
      managePagination
      decoratedRowItems
    />
  );
};

const Template = ({ formId, backFn, nextFn }) => {
  const { isLoading, run } = useAsync();
  const [currentSelectedTemplateId, setCurrentSelectedTemplateId] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    run(getSelectedTemplate({ surveyId: formId }).then((data) => data && setCurrentSelectedTemplateId(data.id)));
  }, []);

  const save = () => {
    setTemplate({ surveyId: formId, templateId: currentSelectedTemplateId })
      .then((res) => {
        nextFn();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar('Sorry, unable to save selection!'));
      });
  };

  return (
    <>
      <StyledTypography variant="h5" customColor={theme.palette.text.heading} bottomGutter={1}>
        Select Template
      </StyledTypography>
      <StyledTypography variant="body" customColor={theme.palette.text.label}>
        Please select one of the following templates
      </StyledTypography>
      <FormContainer>
        <TemplateTable
          setCurrentSelectedTemplateId={setCurrentSelectedTemplateId}
          currentSelectedTemplateId={currentSelectedTemplateId}
        />
      </FormContainer>
      <ActionsContainer
        ContainerComp={checkNewNavEnabled() ? DeerFooterContainer : null}
        loading={isLoading}
        onBackClick={backFn}
        onNextClick={save}
        backText="back"
        nextDisabled={!currentSelectedTemplateId}
      />
    </>
  );
};

const FormContainer = styled.div`
  margin-top: 1.5rem;
  margin-bottom: 5rem;
`;

export default Template;
